/**
 * The third level of the document taxonomy, for one document type.
 *
 *     Financial Documentation      (category)
 *     └─ Bank Statement            (type)
 *        ├─ Checking Statement     (sub-type)  <- this component
 *        └─ Savings Statement      (sub-type)
 *
 * Renders in two modes, driven by `programId`:
 *
 *   null      the ORGANIZATION catalog. Create, edit and deactivate sub-types,
 *             and choose which programs each is offered to.
 *   a program THAT PROGRAM's definition. Each sub-type can be offered or not,
 *             and when offered can be marked required and given the program's own
 *             display name, order, due date and metadata fields. Adding a
 *             sub-type here creates it in the catalog and selects it for this
 *             program in one step — which is what "each program defines its own
 *             sub-types" means in practice.
 *
 * The org owns one row per sub-type and programs layer overrides on top, so a
 * sub-type used by three programs is renamed once, not three times. Same
 * architecture as the per-program member field config.
 *
 * Two rules from the API that the UI has to respect or it will lie to the admin:
 *   - A program with NO selection for a type is offered ALL of that type's
 *     sub-types. So "nothing ticked" must read as "all available", never as
 *     "none available".
 *   - PUT .../config REPLACES a program's rows for the whole type, so every save
 *     sends the complete set, not just the row that changed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  FileText, Layers, Settings2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { getWeaveinApiUrl } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

export interface MetadataField {
  field_id: string;
  field_type: string;
  label: string;
  description?: string;
  required: boolean;
  options?: string[];
  order: number;
}

export interface DocSubtype {
  id: number;
  cbo_id: number;
  doc_type_id: number;
  name: string;
  description: string | null;
  metadata_fields: MetadataField[];
  due_date_rules: any;
  is_notes_required: boolean;
  allowed_file_types: string[] | null;
  max_file_size_mb: number | null;
  is_active: boolean;
  sort_order: number;
  program_ids: number[];
  document_count: number;
  // Present only on program-aware responses.
  is_required: boolean | null;
  effective_name: string | null;
  is_program_selected: boolean | null;
  // RAW overrides, from the program editor endpoint only. null = this program
  // inherits the catalog value. Distinct from the fields above, which on the
  // editor endpoint still hold the ORGANIZATION's values — that separation is
  // what lets a save round-trip an inherited value without pinning it.
  override_display_name?: string | null;
  override_sort_order?: number | null;
  override_due_date_rules?: any;
  override_metadata_fields?: MetadataField[] | null;
}

interface ProgramOption {
  id: number;
  name: string;
}

interface DocSubtypeListProps {
  docTypeId: number;
  docTypeName: string;
  /** null = edit the organization catalog; a number = edit that program's definition. */
  programId: number | null;
  programs: ProgramOption[];
  onError: (message: string) => void;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

// ============================================================================
// METADATA FIELD EDITOR
// ============================================================================

/**
 * Shared by the catalog dialog and the per-program override dialog so the two
 * cannot drift — a program's metadata override has to be the same shape as the
 * value it overrides.
 */
export function MetadataFieldsEditor({
  fields, onChange,
}: {
  fields: MetadataField[];
  onChange: (fields: MetadataField[]) => void;
}) {
  const add = () => onChange([
    ...fields,
    {
      field_id: `field_${Date.now()}`,
      field_type: 'text',
      label: '',
      required: false,
      order: fields.length,
    },
  ]);

  const update = (index: number, patch: Partial<MetadataField>) => {
    const next = [...fields];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Fields captured at upload</Label>
        <Button variant="outline" size="sm" onClick={add}>
          <Plus className="w-3 h-3 mr-1" /> Add Field
        </Button>
      </div>
      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground">No extra fields.</p>
      )}
      {fields.map((field, index) => (
        <div key={field.field_id} className="flex items-start gap-2 p-2 border rounded-md">
          <div className="flex-1 grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={field.label}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder="Field label"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={field.field_type}
                onValueChange={(v) => update(index, { field_type: v })}
              >
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['text', 'textarea', 'number', 'date', 'dropdown', 'checkbox'].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Checkbox
                checked={field.required}
                onCheckedChange={(v) => update(index, { required: Boolean(v) })}
              />
              <Label className="text-xs">Required</Label>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(fields.filter((_, i) => i !== index))}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

const emptyCatalogForm = (docTypeId: number) => ({
  doc_type_id: docTypeId,
  name: '',
  description: '',
  metadata_fields: [] as MetadataField[],
  due_date_rules: { type: 'none' } as any,
  is_notes_required: false,
  // null on both = inherit the parent type's rules. Not an empty list, which
  // would reject every file, and not 0, which would reject every size.
  allowed_file_types: null as string[] | null,
  max_file_size_mb: null as number | null,
  sort_order: 0,
  program_ids: [] as number[],
});

export function DocSubtypeList({
  docTypeId, docTypeName, programId, programs, onError,
}: DocSubtypeListProps) {
  const [expanded, setExpanded] = useState(false);
  const [subtypes, setSubtypes] = useState<DocSubtype[] | null>(null);
  const [saving, setSaving] = useState(false);

  // Catalog dialog
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DocSubtype | null>(null);
  const [form, setForm] = useState<any>(emptyCatalogForm(docTypeId));

  // Per-program override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState<DocSubtype | null>(null);
  const [overrideForm, setOverrideForm] = useState<any>(null);

  const fetchSubtypes = useCallback(async () => {
    try {
      const url = programId
        ? `/api/doc-subtypes/programs/${programId}/config?doc_type_id=${docTypeId}`
        : `/api/doc-subtypes?doc_type_id=${docTypeId}&include_inactive=true`;
      const res = await fetch(getWeaveinApiUrl(url), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load document sub-types');
      const data = await res.json();
      setSubtypes(Array.isArray(data) ? data : []);
    } catch (e: any) {
      onError(e.message);
      setSubtypes([]);
    }
  }, [docTypeId, programId, onError]);

  useEffect(() => {
    // Refetch on a mode change, but only if the admin has this type open —
    // switching programs must not fire a request for every type on the page.
    if (expanded) fetchSubtypes();
  }, [expanded, fetchSubtypes]);

  // ------------------------------------------------------------------------
  // Catalog writes
  // ------------------------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyCatalogForm(docTypeId),
      // Creating from inside a program pre-selects that program, so the sub-type
      // an admin just defined for it is actually offered there.
      program_ids: programId ? [programId] : [],
    });
    setCatalogDialogOpen(true);
  };

  const openEdit = (st: DocSubtype) => {
    setEditing(st);
    setForm({
      doc_type_id: st.doc_type_id,
      name: st.name,
      description: st.description || '',
      metadata_fields: st.metadata_fields || [],
      due_date_rules: st.due_date_rules || { type: 'none' },
      is_notes_required: st.is_notes_required,
      allowed_file_types: st.allowed_file_types,
      max_file_size_mb: st.max_file_size_mb,
      sort_order: st.sort_order,
      program_ids: st.program_ids || [],
    });
    setCatalogDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        getWeaveinApiUrl(editing ? `/api/doc-subtypes/${editing.id}` : '/api/doc-subtypes'),
        {
          method: editing ? 'PUT' : 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify(form),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save document sub-type');
      }
      setCatalogDialogOpen(false);
      await fetchSubtypes();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (st: DocSubtype) => {
    if (!confirm(`Deactivate "${st.name}"? Documents already filed under it are preserved.`)) return;
    try {
      await fetch(getWeaveinApiUrl(`/api/doc-subtypes/${st.id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      await fetchSubtypes();
    } catch (e: any) {
      onError(e.message);
    }
  };

  // ------------------------------------------------------------------------
  // Per-program writes
  // ------------------------------------------------------------------------

  /**
   * Send this program's WHOLE selection for the type. The endpoint replaces the
   * set, so building the payload from anything less than the full local list
   * would silently drop the rows it omitted.
   */
  const persistProgramConfig = async (rows: DocSubtype[]) => {
    if (!programId) return;
    setSaving(true);
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/doc-subtypes/programs/${programId}/config`),
        {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            doc_type_id: docTypeId,
            // Only the RAW overrides. Sending the merged values back would
            // convert everything this program inherits into a pinned override,
            // so the sub-type would stop following the catalog the first time
            // anyone ticked a checkbox — a rename or a due-date change in
            // Administration would then silently not reach this program.
            subtypes: rows
              .filter((r) => r.is_program_selected)
              .map((r) => ({
                doc_subtype_id: r.id,
                is_required: r.is_required,
                display_name: r.override_display_name ?? null,
                sort_order: r.override_sort_order ?? null,
                due_date_rules: r.override_due_date_rules ?? null,
                metadata_fields: r.override_metadata_fields ?? null,
              })),
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save the program configuration');
      }
      await fetchSubtypes();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleOffered = (st: DocSubtype) => {
    if (!subtypes) return;
    const next = subtypes.map((r) =>
      r.id === st.id
        ? {
            ...r,
            is_program_selected: !r.is_program_selected,
            // Dropping a sub-type drops its overrides with it; keeping them
            // would resurrect a stale "required" the next time it is re-offered.
            is_required: r.is_program_selected ? null : r.is_required,
          }
        : r,
    );
    setSubtypes(next);
    persistProgramConfig(next);
  };

  const toggleRequired = (st: DocSubtype) => {
    if (!subtypes) return;
    const next = subtypes.map((r) =>
      r.id === st.id ? { ...r, is_required: !r.is_required } : r,
    );
    setSubtypes(next);
    persistProgramConfig(next);
  };

  const openOverride = (st: DocSubtype) => {
    setOverrideTarget(st);
    // Prefill with the value in force — the program's override where it has one,
    // otherwise the catalog's, so the admin edits what they can see. On save each
    // field is compared back against the catalog and only a genuine difference
    // becomes an override.
    setOverrideForm({
      display_name: st.override_display_name ?? st.name,
      sort_order: st.override_sort_order ?? st.sort_order,
      due_date_rules: st.override_due_date_rules ?? st.due_date_rules ?? { type: 'none' },
      metadata_fields: st.override_metadata_fields ?? st.metadata_fields ?? [],
    });
    setOverrideDialogOpen(true);
  };

  const saveOverride = async () => {
    if (!subtypes || !overrideTarget) return;
    const sameJson = (a: any, b: any) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    const next = subtypes.map((r) =>
      r.id === overrideTarget.id
        ? {
            ...r,
            is_program_selected: true,
            // A field left equal to the catalog's stays an inherit (null), so it
            // keeps following the organization after a rename or a rule change.
            override_display_name:
              overrideForm.display_name && overrideForm.display_name !== r.name
                ? overrideForm.display_name
                : null,
            override_sort_order:
              overrideForm.sort_order !== r.sort_order ? overrideForm.sort_order : null,
            override_due_date_rules: sameJson(overrideForm.due_date_rules, r.due_date_rules)
              ? null
              : overrideForm.due_date_rules,
            override_metadata_fields:
              overrideForm.metadata_fields?.length && !sameJson(overrideForm.metadata_fields, r.metadata_fields)
                ? overrideForm.metadata_fields
                : null,
            effective_name: overrideForm.display_name || r.name,
          }
        : r,
    );
    setSubtypes(next);
    setOverrideDialogOpen(false);
    await persistProgramConfig(next);
  };

  // ------------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------------

  const rows = subtypes || [];
  const offeredCount = programId
    ? rows.filter((r) => r.is_program_selected).length
    : rows.filter((r) => r.is_active).length;
  const nothingSelected = Boolean(programId) && rows.length > 0 && offeredCount === 0;

  return (
    <div className="mt-2 ml-6 border-l pl-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Layers className="w-3 h-3" />
        {subtypes === null
          ? 'Sub-types'
          : rows.length === 0
            ? 'Sub-types (none)'
            : `Sub-types (${offeredCount} of ${rows.length})`}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {/* The API treats "no selection" as "all available". Saying so is the
              difference between an admin reading this as configured-empty and
              reading it as not-yet-narrowed. */}
          {nothingSelected && (
            <p className="text-xs text-muted-foreground">
              None ticked, so <strong>every</strong> sub-type of {docTypeName} is available to
              this program. Tick the ones that apply to narrow it.
            </p>
          )}

          {rows.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {docTypeName} has no sub-types. Uploads pick the type itself, exactly as before.
            </p>
          )}

          {rows.map((st) => (
            <div
              key={st.id}
              className={`flex items-center justify-between gap-2 p-2 rounded-md border text-sm ${
                !st.is_active ? 'opacity-60 bg-muted/50' : 'bg-background'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {programId ? (
                  <Checkbox
                    checked={Boolean(st.is_program_selected)}
                    onCheckedChange={() => toggleOffered(st)}
                  />
                ) : (
                  <FileText className="w-3 h-3 text-muted-foreground" />
                )}
                <div className="min-w-0">
                  <div className="truncate">
                    {st.effective_name || st.name}
                    {st.effective_name && st.effective_name !== st.name && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (org: {st.name})
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    {st.document_count > 0 && <span>{st.document_count} docs</span>}
                    {st.metadata_fields?.length > 0 && (
                      <span>{st.metadata_fields.length} fields</span>
                    )}
                    {(() => {
                      const rules = st.override_due_date_rules ?? st.due_date_rules;
                      return rules?.type === 'relative' && rules?.days_after_enrollment ? (
                        <span>due +{rules.days_after_enrollment}d</span>
                      ) : null;
                    })()}
                    {!programId && st.program_ids?.length > 0 && (
                      <span>
                        {st.program_ids.length === 1
                          ? programs.find((p) => p.id === st.program_ids[0])?.name || '1 program'
                          : `${st.program_ids.length} programs`}
                      </span>
                    )}
                    {!st.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {programId && st.is_program_selected && (
                  <>
                    <label className="flex items-center gap-1 text-xs mr-1">
                      <Checkbox
                        checked={Boolean(st.is_required)}
                        onCheckedChange={() => toggleRequired(st)}
                      />
                      Required
                    </label>
                    <Button variant="ghost" size="sm" onClick={() => openOverride(st)} title="Configure for this program">
                      <Settings2 className="w-3 h-3" />
                    </Button>
                  </>
                )}
                {!programId && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(st)}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deactivate(st)}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={openCreate} disabled={saving}>
            <Plus className="w-3 h-3 mr-1" /> Add Sub-type
          </Button>
        </div>
      )}

      {/* ================================================================ */}
      {/* CATALOG DIALOG                                                    */}
      {/* ================================================================ */}
      <Dialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Sub-type' : 'New Sub-type'}</DialogTitle>
            <DialogDescription>
              A sub-type of {docTypeName}. It belongs to the organization&apos;s catalog;
              each program then chooses whether to offer it and can give it its own
              name, order, due date and required flag.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Checking Statement"
                />
              </div>
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_notes_required}
                onCheckedChange={(v) => setForm({ ...form, is_notes_required: v })}
              />
              <Label>Require notes on upload</Label>
              <span className="text-xs text-muted-foreground">
                (adds to the type&apos;s rule; it cannot waive it)
              </span>
            </div>

            <div className="space-y-2">
              <Label>Due date</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={form.due_date_rules?.type || 'none'}
                  onValueChange={(v) => setForm({ ...form, due_date_rules: { ...form.due_date_rules, type: v } })}
                >
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No due date</SelectItem>
                    <SelectItem value="relative">Days after enrollment</SelectItem>
                    <SelectItem value="fixed">Fixed date</SelectItem>
                  </SelectContent>
                </Select>
                {form.due_date_rules?.type === 'relative' && (
                  <Input
                    type="number"
                    className="w-24"
                    value={form.due_date_rules?.days_after_enrollment ?? ''}
                    onChange={(e) => setForm({
                      ...form,
                      due_date_rules: {
                        ...form.due_date_rules,
                        days_after_enrollment: Number(e.target.value) || null,
                      },
                    })}
                    placeholder="days"
                  />
                )}
                {form.due_date_rules?.type === 'fixed' && (
                  <Input
                    type="date"
                    className="w-44"
                    value={form.due_date_rules?.fixed_date || ''}
                    onChange={(e) => setForm({
                      ...form,
                      due_date_rules: { ...form.due_date_rules, fixed_date: e.target.value },
                    })}
                  />
                )}
              </div>
            </div>

            <MetadataFieldsEditor
              fields={form.metadata_fields}
              onChange={(fields) => setForm({ ...form, metadata_fields: fields })}
            />

            <div className="space-y-2">
              <Label>Program Availability</Label>
              {programs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active programs, so this sub-type is offered everywhere.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    {form.program_ids.length === 0
                      ? 'Offered to every program. Tick programs to narrow it.'
                      : 'Offered only to the ticked programs.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {programs.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={form.program_ids.includes(p.id)}
                          onCheckedChange={() => setForm({
                            ...form,
                            program_ids: form.program_ids.includes(p.id)
                              ? form.program_ids.filter((id: number) => id !== p.id)
                              : [...form.program_ids, p.id],
                          })}
                        />
                        <span className="truncate" title={p.name}>{p.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name || saving}>
              {editing ? 'Update' : 'Create'} Sub-type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================ */}
      {/* PER-PROGRAM OVERRIDE DIALOG                                       */}
      {/* ================================================================ */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure for this program</DialogTitle>
            <DialogDescription>
              {overrideTarget?.name} — these settings apply to this program only. The
              organization&apos;s catalog is unchanged, and other programs keep their own.
            </DialogDescription>
          </DialogHeader>

          {overrideForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Display name</Label>
                  <Input
                    value={overrideForm.display_name}
                    onChange={(e) => setOverrideForm({ ...overrideForm, display_name: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave as the catalog name to keep following it after a rename.
                  </p>
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={overrideForm.sort_order}
                    onChange={(e) => setOverrideForm({
                      ...overrideForm, sort_order: Number(e.target.value) || 0,
                    })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Due date</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={overrideForm.due_date_rules?.type || 'none'}
                    onValueChange={(v) => setOverrideForm({
                      ...overrideForm,
                      due_date_rules: { ...overrideForm.due_date_rules, type: v },
                    })}
                  >
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No due date</SelectItem>
                      <SelectItem value="relative">Days after enrollment</SelectItem>
                      <SelectItem value="fixed">Fixed date</SelectItem>
                    </SelectContent>
                  </Select>
                  {overrideForm.due_date_rules?.type === 'relative' && (
                    <Input
                      type="number"
                      className="w-24"
                      value={overrideForm.due_date_rules?.days_after_enrollment ?? ''}
                      onChange={(e) => setOverrideForm({
                        ...overrideForm,
                        due_date_rules: {
                          ...overrideForm.due_date_rules,
                          days_after_enrollment: Number(e.target.value) || null,
                        },
                      })}
                      placeholder="days"
                    />
                  )}
                  {overrideForm.due_date_rules?.type === 'fixed' && (
                    <Input
                      type="date"
                      className="w-44"
                      value={overrideForm.due_date_rules?.fixed_date || ''}
                      onChange={(e) => setOverrideForm({
                        ...overrideForm,
                        due_date_rules: { ...overrideForm.due_date_rules, fixed_date: e.target.value },
                      })}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  A required sub-type with a due date becomes a document requirement
                  automatically when a member is enrolled in this program.
                </p>
              </div>

              <MetadataFieldsEditor
                fields={overrideForm.metadata_fields}
                onChange={(fields) => setOverrideForm({ ...overrideForm, metadata_fields: fields })}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveOverride} disabled={saving}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
